import {
  Component,
  Input,
  Output,
  EventEmitter,
  ViewChild,
  ChangeDetectorRef,
  ApplicationRef,
  OnChanges,
  SimpleChanges
} from '@angular/core';
import { CurrencyPipe } from '@angular/common';
import { Router } from '@angular/router';
import { BsModalService } from 'ngx-bootstrap/modal';
import { filter, map, take } from 'rxjs/operators';
import { AlertModalComponent } from '@components/alert-modal/alert-modal.component';
import { UnlockWithPointsModalComponent } from '@components/unlock-with-points-modal/unlock-with-points-modal.component';
import type { LoyaltyPaymentOrderResponse } from '@components/unlock-with-points-modal/unlock-with-points-modal.interface';
import { PAYMENT_FOR_TYPE, PORTFOLIO_CONSTS, Fields } from '@constants/constant';
import { MUSIC_PLAYER_CONST, PURCHASE_MASTER_DATA_TEXT, } from '@components/player-modal/constant';
import {
  PURCHASE_TEXT_MAP,
  PORTFOLIO_UNLOCK_LOYALTY_TEXT
} from '@modules/edit-preference/components/portfolio/portfolio.contants';
import { UNLOCK_WITH_POINTS_BTN } from '@constants/unlock-with-points-btn.constant';
import { LOCKED_POST_MESSAGES } from '@modules/dashboard/constants/home-strings.constant';
import { LOYALTY_POINTS_TEXT } from '@modules/wallet/component/loyalty-points/loyalty-points.constants';
import type { LoyaltyPointsPaymentUser } from '@modules/wallet/component/loyalty-points/loyalty-points.interface';
import { AuthService } from '@core/auth.service';
import { API_URLS } from '@core/api-url.enum';
import { ApiCallService } from '@services/api-call.service';
import { ApiCacheService, CacheTypes } from '@core/cache/api-cache.service';
import { CommonService } from '@modules/shared/services/common.service';
import { PermissionService } from '@shared/services/permissions.service';
import { LoginDialogComponent } from '@shared/components/login-dialog/login-dialog.component';
import {
  htmlToPlainTextForTitle,
  truncateDescription
} from '@utils/common.utils';

@Component({
  selector: 'app-portfolio-unlock-modal',
  templateUrl: './portfolio-unlock-modal.component.html',
  styleUrls: ['./portfolio-unlock-modal.component.scss']
})
export class PortfolioUnlockModalComponent implements OnChanges {
  @ViewChild('unlockModal') unlockModal: AlertModalComponent;

  @Input() selectedItem: any = null;
  @Input() albumHeaderData: any = null;
  @Input() userProfile: any = null;
  @Input() userSubscriptionList: any[] = [];
  @Input() pUserSubscriptionList: any[] = [];
  @Input() contentType: string = PAYMENT_FOR_TYPE.MUSIC_PORTFOLIO;
  @Input() headerType: string = MUSIC_PLAYER_CONST.MUSIC;
  @Input() userId: string = '';
  @Input() isLocked: boolean = true;
  @Input() unlockModalHeader: string = 'Unlock Music';
  @Input() portfolioType: string = PORTFOLIO_CONSTS.TYPE.MUSIC;
  @Input() userName: string = '';
  @Input() portfolioTypeId: string = '';
  @Input() isIndividualPortfolio: boolean = false;
  @Input() isAlbumLocking: boolean = false;
  @Input() isAllowedToRedirect = true;
  @Input() isLivestreamContext = false;
  /** When set, called before navigating to loyalty-points checkout (e.g. close player modal). */
  @Input() onLoyaltyPointsPurchaseNavigate?: () => void;

  @Output() purchaseIndividual = new EventEmitter<void>();
  @Output() purchaseSubscription = new EventEmitter<void>();
  @Output() purchaseNft = new EventEmitter<void>();
  @Output() closeModal = new EventEmitter<void>();
  @Output() unlockedWithPoints = new EventEmitter<void>();
  @Output() switchToAlbumPurchase = new EventEmitter<void>();

  readonly PAYMENT_FOR_TYPE = PAYMENT_FOR_TYPE;
  readonly MUSIC_PLAYER_CONST = MUSIC_PLAYER_CONST;
  readonly Fields = Fields;
  readonly LOCKED_POST_MESSAGES = LOCKED_POST_MESSAGES;
  readonly LOYALTY_POINTS_TEXT = LOYALTY_POINTS_TEXT;
  readonly UNLOCK_WITH_POINTS_BTN = UNLOCK_WITH_POINTS_BTN;
  readonly PORTFOLIO_UNLOCK_LOYALTY_TEXT = PORTFOLIO_UNLOCK_LOYALTY_TEXT;
  readonly isWhiteLabelWebsite = this.authService.isWhiteLabelWebsite();
  PURCHASE_MASTER_DATA_TEXT = PURCHASE_MASTER_DATA_TEXT;
  displayItem: any = null;
  displayIsAlbumLocking: boolean = false;
  displayHeader: string = '';
  private isModalVisible: boolean = false;

  constructor(
    private readonly cdr: ChangeDetectorRef,
    private readonly appRef: ApplicationRef,
    private readonly currencyPipe: CurrencyPipe,
    private readonly modalService: BsModalService,
    private readonly apiCallService: ApiCallService,
    private readonly apiCacheService: ApiCacheService,
    private readonly authService: AuthService,
    private readonly ps: PermissionService,
    private readonly cs: CommonService,
    private readonly router: Router
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (this.isModalVisible) {
      return;
    }
    if (changes['selectedItem'] || changes['albumHeaderData'] || changes['isAlbumLocking']) {
      this.syncDisplayFromInputs();
    }
    if (changes['unlockModalHeader']) {
      this.displayHeader = this.unlockModalHeader;
    }
  }

  private syncDisplayFromInputs(): void {
    this.displayItem = this.selectedItem;
    this.displayIsAlbumLocking = this.computeIsAlbumLocking(this.selectedItem);
    this.displayHeader = this.unlockModalHeader;
  }

  private computeIsAlbumLocking(item: any): boolean {
    if (typeof item?.isAlbumLocking === 'boolean') {
      return item.isAlbumLocking;
    }
    if (item?.sk && this.albumHeaderData?.sk) {
      return item.sk === this.albumHeaderData.sk;
    }
    return this.isAlbumLocking;
  }

  unlockModalOpen(item?: any): void {
    const nextItem = item ?? this.selectedItem;
    this.displayItem = nextItem;
    this.displayIsAlbumLocking = this.computeIsAlbumLocking(nextItem);
    this.displayHeader = this.unlockModalHeader;
    this.isModalVisible = true;
    this.cdr.detectChanges();
    this.unlockModal?.openModal();
  }

  unlockModalClose(): void {
    this.isModalVisible = false;
    this.displayItem = null;
    this.displayIsAlbumLocking = false;
    this.displayHeader = '';
    this.unlockModal?.closeModal();
  }

  get isOuterUnlockDisabled(): boolean {
    return (
      this.displayItem?.unlocked_through?.hasOwnProperty(
        'is_outer_unlocked'
      ) && !this.displayItem?.unlocked_through?.is_outer_unlocked
    );
  }

  get hasIndividualPrice(): boolean {
    return !!this.displayItem?.lock_through?.individual?.individual_price;
  }

  get hasSubscription(): boolean {
    return !!this.displayItem?.lock_through?.subscription?.length;
  }

  get hasNft(): boolean {
    return !!this.displayItem?.lock_through?.nft?.length;
  }

  get showOrBeforeSubscription(): boolean {
    return this.hasIndividualPrice && this.hasSubscription;
  }

  get showOrBeforeNft(): boolean {
    return (this.hasIndividualPrice || this.hasSubscription) && this.hasNft;
  }

  get showOrBeforeLoyalty(): boolean {
    return (
      (this.hasIndividualPrice ||
        this.hasSubscription ||
        this.hasNft) &&
      !!this.displayItem?.lock_through?.individual?.loyalty_points &&
      this.isWhiteLabelWebsite
    );
  }

  get showBuyEntireAlbum(): boolean {
    const albumLockThrough = this.albumHeaderData?.lock_through;
    const hasAlbumUnlockOptions = !!(
      albumLockThrough?.individual?.individual_price ||
      albumLockThrough?.subscription?.length ||
      albumLockThrough?.nft?.length
    );
    const alreadyViewingAlbum =
      !!this.displayItem?.sk &&
      !!this.albumHeaderData?.sk &&
      this.displayItem.sk === this.albumHeaderData.sk;
    return (
      !!this.albumHeaderData?.isLocked &&
      hasAlbumUnlockOptions &&
      !alreadyViewingAlbum
    );
  }

  onPurchaseIndividual(): void {
    this.purchaseIndividual.emit();
  }

  onPurchaseSubscription(): void {
    this.purchaseSubscription.emit();
  }

  onPurchaseNft(): void {
    this.purchaseNft.emit();
  }

  onModalCanceled(): void {
    this.isModalVisible = false;
    this.displayItem = null;
    this.displayIsAlbumLocking = false;
    this.displayHeader = '';
    this.closeModal.emit();
  }

  handleUnlockContentClose(): void {
    this.unlockModalClose();
  }

  openUnlockWithPointsModal(): void {
    if (!this.cs.isBrowser) {
      return;
    }
    if (!this.authService.isAuthenticated()) {
      this.modalService.show(LoginDialogComponent, {});
      return;
    }

    const cb = (): void => {
      const item = this.displayItem ?? this.selectedItem;
      const creator = this.userProfile;
      const loyaltyPoints = item?.lock_through?.individual?.loyalty_points;
      const creatorGsi4pk = creator?.gsi4pk;

      if (
        !item?.sk ||
        !creatorGsi4pk ||
        !loyaltyPoints
      ) {
        return;
      }

      this.apiCacheService
        .get(API_URLS.getUsers, CacheTypes.REFRESH_EVERY_N)
        .pipe(
          filter((res: { data?: { gsi4pk?: string } }) => !!res?.data?.gsi4pk),
          take(1)
        )
        .subscribe({
          next: (res: { data: { gsi4pk: string } }) => {
            const fanProfile = res.data;
            const creatorUser = this.getCreatorUserForLoyaltyPayment(creator);
            const plainDesc = this.getPortfolioDescriptionSnippet(item);

            this.unlockModalClose();
            this.cdr.detectChanges();

            this.modalService.show(UnlockWithPointsModalComponent, {
              initialState: {
                headerTitle:
                  PORTFOLIO_UNLOCK_LOYALTY_TEXT.UNLOCK_PORTFOLIO_WITH_POINTS_MODAL_HEADER,
                contentTitle: item?.title ?? '',
                contentDescription: plainDesc,
                pointsCost: loyaltyPoints,
                creatorId: creatorGsi4pk,
                fanId: fanProfile.gsi4pk,
                onUnlockSuccess: () => {
                  this.apiCacheService.clearAllCache();
                  this.unlockedWithPoints.emit();
                },
                creatorUserForPayment: creatorUser,
                loyaltyPointsPurchaseReturnUrl: this.router.url,
                isPortfolioPointsPurchase: true,
                onBeforeLoyaltyPointsPurchaseNavigate: () =>
                  this.onLoyaltyPointsPurchaseNavigate(),
                createLoyaltyPaymentOrder: () =>
                  this.apiCallService
                    .post(
                      API_URLS.portfolioContentPurchase,
                      {
                        creator_id: creatorGsi4pk,
                        content_sk: item.sk,
                        content_type: this.displayIsAlbumLocking
                          ? `ALBUM_${this.portfolioType}`
                          : this.portfolioType,
                        is_loyalty_points_payment: true
                      },
                      false
                    )
                    .pipe(
                      map(
                        (r: {
                          status?: boolean;
                          data?: { order_sk?: string };
                          message?: string;
                        }): LoyaltyPaymentOrderResponse => ({
                          status: r?.status,
                          order_sk: r?.data?.order_sk,
                          message: r?.message
                        })
                      )
                    )
              },
              class: 'modal-dialog-centered unlock-with-points-modal-bs'
            });
          }
        });
    };

    this.ps.checkDelegateUserPermission(cb.bind(this));
  }

  private getCreatorUserForLoyaltyPayment(
    creator: any
  ): LoyaltyPointsPaymentUser | null {
    const gsi4pk = creator?.gsi4pk;
    if (!gsi4pk) {
      return null;
    }
    return {
      gsi4pk,
      first_name: creator.first_name,
      last_name: creator.last_name,
      user_name: creator.user_name
    };
  }

  private getPortfolioDescriptionSnippet(item: any): string {
    const raw = item?.description;
    const plain = htmlToPlainTextForTitle(raw).trim();
    return truncateDescription(plain, 4, 280).trim();
  }

  getPurchaseTextBtn(): string {
    const price = this.displayItem?.price
      ? this.displayItem.price
      : this.displayItem?.lock_through?.individual?.individual_price;
    if (this.displayIsAlbumLocking) {
      return (
        PURCHASE_TEXT_MAP[this.portfolioType].MASTER.INDIVIDUAL_PURCHASE +
        ' ' +
        this.currencyPipe.transform(
          price,
          this.displayItem?.currency_symbol
        )
      );
    }
    return (
      PURCHASE_TEXT_MAP[this.portfolioType].SINGLE.INDIVIDUAL_PURCHASE +
      ' ' +
      this.currencyPipe.transform(price, this.displayItem?.currency_symbol)
    );
  }

  getSubscribeTextBtn(): string {
    if (this.displayIsAlbumLocking) {
      return PURCHASE_TEXT_MAP[this.portfolioType].MASTER.SUBSCRIPTION;
    }
    return PURCHASE_TEXT_MAP[this.portfolioType].SINGLE.SUBSCRIPTION;
  }

  getNFTTextBtn(): string {
    if (this.displayIsAlbumLocking) {
      return PURCHASE_TEXT_MAP[this.portfolioType].MASTER.NFT;
    }
    return PURCHASE_TEXT_MAP[this.portfolioType].SINGLE.NFT;
  }

  purchaseMasterData(event?: Event): void {
    if (!this.albumHeaderData || !this.albumHeaderData.isLocked) {
      return;
    }

    if (!this.authService.isAuthenticated()) {
      this.unlockModalClose();
      this.modalService.show(LoginDialogComponent, {});
      return;
    }

    event?.stopPropagation();

    this.displayItem = this.albumHeaderData;
    this.displayIsAlbumLocking = true;
    this.displayHeader = this.buildAlbumHeader();
    this.cdr.detectChanges();
    this.appRef.tick();

    this.switchToAlbumPurchase.emit();
  }

  private buildAlbumHeader(): string {
    const typeLabel =
      this.portfolioType.charAt(0).toUpperCase() +
      this.portfolioType.slice(1).toLowerCase();
    const suffix =
      this.portfolioType.toLowerCase() ===
      PORTFOLIO_CONSTS.TYPE.MUSIC.toLowerCase()
        ? MUSIC_PLAYER_CONST.SPACE_ALBUM
        : MUSIC_PLAYER_CONST.SPACE_SERIES;
    return 'Unlock ' + typeLabel + suffix;
  }
}
